<script setup lang="ts">
import type { ContentDetailsTest, ContentDetailsTestItem, ReportContentVisit } from '~/models/Report'
import type { ReportFilters } from '~/models/ReportFilters'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck, faExclamation } from '@fortawesome/pro-solid-svg-icons'
import { resolveUserName } from '~/utils/reportUtils'
import { getTaskStatusIcon } from '~/utils/reportIcons'
import useProductStore from '~/stores/product'
import { useTime } from '~/composables/useTime'
import useGroupsStore from '~/stores/groups'

const props = defineProps<{
  content: BaseItem
  visit?: ReportContentVisit
  filters?: ReportFilters
  selectedUserId?: string
  relation?: ContentDetailsTest
}>()

const { t } = useI18n()
const { formatReportTimestamp } = useTime()
const { getProductFromResource } = useProductStore()

const product = computed(() => getProductFromResource(props.content))
const subjectOrProductName = computed(() => {
  if (!product.value)
    return ''
  return product.value.subjects?.length ? t(`metadata.subjects.${product.value.subjects[0]}`) : ''
})

const relatedGroupMember = computed(() => {
  if (!props.selectedUserId || !props.filters?.selectedGroup?.groupId) {
    return undefined
  }
  const groupsStore = useGroupsStore()
  return groupsStore.findGroupMembersById(props.filters.selectedGroup.groupId)
    .find(member => member.userId === props.selectedUserId)
})

const selectedUserTask = computed(() => {
  if (!props.selectedUserId || !props.relation?.testActivities) {
    return undefined
  }
  return props.relation.testActivities.find(activity => activity?.user?.userId === props.selectedUserId)
})

const studentName = computed(() => resolveUserName(relatedGroupMember.value, selectedUserTask.value?.user))

const progressStatus = computed(() => {
  const total = totalTaskCount.value
  if (total === 0) {
    return t('reports.status.notStarted')
  }

  let tasksWithData = 0
  for (let i = 0; i < total; i++) {
    const taskResult = getTaskResultByIndex(i)
    if (taskResult !== null) {
      tasksWithData++
    }
  }

  if (tasksWithData === 0) {
    return t('reports.status.notStarted')
  } else if (tasksWithData === total) {
    return t('reports.status.completed')
  } else {
    return t('reports.status.started')
  }
})

const progressStatusBadgeClass = computed(() => {
  const status = progressStatus.value
  if (status === t('reports.status.completed')) {
    return 'bg-green-40 text-white'
  } else if (status === t('reports.status.started')) {
    return 'bg-orange-40 text-white'
  } else {
    return 'bg-gray-40 text-white'
  }
})

const progressStatusIcon = computed(() => {
  const status = progressStatus.value
  if (status === t('reports.status.completed')) {
    return faCheck
  } else if (status === t('reports.status.started')) {
    return faExclamation
  } else {
    return null
  }
})

const lastActivity = computed(() => formatReportTimestamp(selectedUserTask.value?.providedTimestamp))
const sortedTaskItems = computed(() => {
  if (!selectedUserTask.value?.items) {
    return []
  }
  return selectedUserTask.value.items.slice().sort((a, b) => a.order - b.order)
})

const totalTaskCount = computed(() => {
  if (!props.relation?.testActivities) {
    return 5
  }
  let maxCount = 0
  props.relation.testActivities.forEach((activity) => {
    if (activity.items && activity.items.length > 0) {
      maxCount = Math.max(maxCount, activity.items.length)
    }
  })
  return maxCount || 5
})

const labelText = computed(() => t(`labels.${props.content.label || props.content.contentTypeIdentifier}`))

const getTaskResultByIndex = (taskIndex: number): ContentDetailsTestItem | null => {
  if (!sortedTaskItems.value || taskIndex >= sortedTaskItems.value.length) {
    return null
  }
  return sortedTaskItems.value[taskIndex] || null
}

const getTaskStatus = (result: ContentDetailsTestItem | null) => {
  const iconResult = getTaskStatusIcon(
    result?.result || null,
    t,
    result?.score,
    result?.targetScore
  )
  return {
    icon: iconResult.icon,
    color: iconResult.class
  }
}
</script>

<template>
  <div>
    <div class="mb-1 flex flex-wrap items-center gap-2">
      <span
        v-if="content.label"
        class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm"
        v-text="labelText"
      />
      <span
        v-if="subjectOrProductName"
        class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm"
        v-text="subjectOrProductName"
      />
    </div>

    <div class="bg-gray-5 rounded-lg py-4">
      <div class="space-y-3 text-base">
        <div v-if="studentName" class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32">{{ t('reports.formwriter.student') }}:</span>
          <span class="text-gray-90">{{ studentName }}</span>
        </div>

        <div v-if="content.title" class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32">{{ t('reports.task.article') }}:</span>
          <span class="text-gray-90">{{ content.title }}</span>
        </div>

        <div class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32">{{ t('reports.task.progression') }}:</span>
          <span class="text-gray-90">{{ progressStatus }}</span>
        </div>

        <div v-if="lastActivity" class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32">{{ t('reports.formwriter.lastActivity') }}:</span>
          <span class="text-gray-90">{{ lastActivity }}</span>
        </div>
      </div>
    </div>

    <div class="space-y-3">
      <div>
        <span class="inline-flex items-center gap-2 rounded p-2 text-sm font-normal" :class="progressStatusBadgeClass">
          {{ progressStatus }}
          <FontAwesomeIcon
            v-if="progressStatusIcon"
            :icon="progressStatusIcon"
            class="h-4 w-4"
          />
        </span>
      </div>

      <div v-if="totalTaskCount > 0">
        <div
          v-for="taskIndex in totalTaskCount"
          :key="taskIndex"
          class="border-gray-20 flex items-center border-b py-3 last:border-b-0"
        >
          <div class="mr-4 flex items-center">
            <FontAwesomeIcon
              :icon="getTaskStatus(getTaskResultByIndex(taskIndex - 1)).icon"
              :class="getTaskStatus(getTaskResultByIndex(taskIndex - 1)).color"
              class="h-8 w-8 text-2xl"
            />
          </div>

          <div class="flex-1">
            <div class="text-gray-90 font-medium">
              {{ t('reports.task.taskItem') }} {{ taskIndex }}
            </div>
            <div v-if="getTaskResultByIndex(taskIndex - 1)" class="text-gray-70 text-sm">
              {{ t('reports.task.score') }}: {{ getTaskResultByIndex(taskIndex - 1)?.score }}/{{ getTaskResultByIndex(taskIndex - 1)?.targetScore }}
            </div>
          </div>
        </div>
      </div>

      <div v-else class="bg-gray-5 border-gray-10 rounded-lg border p-6 text-center">
        <div class="text-gray-70">
          {{ t('reports.noTaskData') }}
        </div>
      </div>
    </div>
  </div>
</template>
